import React, { useState, useEffect } from "react";
import { Bell, X } from "lucide-react";
import { toast } from "sonner";
import {
    isPushSupported,
    isIosDevice,
    isStandalonePwa,
    getNotificationPermission,
    subscribeUserToPush,
    sendTestPushNotification,
} from "@/lib/pushNotifications";
import NotificationModal from "./NotificationModal";

export default function NotificationPromptBanner() {
    const [visible, setVisible] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!isPushSupported()) return;

        // Don't show if already granted
        if (getNotificationPermission() === "granted") return;

        // Don't show if dismissed in last 7 days
        const dismissed = localStorage.getItem("push_prompt_dismissed");
        if (dismissed && Date.now() - parseInt(dismissed, 10) < 7 * 24 * 60 * 60 * 1000) {
            return;
        }

        // On iOS, only show banner if already running in standalone PWA, or show install guidance
        const isIos = isIosDevice();
        const isStandalone = isStandalonePwa();
        if (isIos && !isStandalone) {
            // Let the PWA install banner take precedence on iOS
            return;
        }

        const timer = setTimeout(() => {
            setVisible(true);
        }, 2000);

        return () => clearTimeout(timer);
    }, []);

    const handleDismiss = () => {
        setVisible(false);
        localStorage.setItem("push_prompt_dismissed", Date.now().toString());
    };

    const handleEnableQuick = async () => {
        setLoading(true);
        try {
            await subscribeUserToPush();
            setVisible(false);
            toast.success("Gate & society push alerts enabled! 🔔");
            try {
                await sendTestPushNotification();
            } catch (e) {
                console.log("Welcome notification note:", e);
            }
        } catch (err) {
            // If direct prompt fails or needs explanation, open full modal
            setModalOpen(true);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            {visible && (
                <div
                    className="fixed right-3 left-3 sm:left-auto sm:right-6 sm:w-96 z-45 bg-[#1C1B1A] text-white p-3.5 rounded-2xl shadow-2xl border border-white/10 flex items-center justify-between gap-3 animate-fade-up bottom-20 sm:bottom-6"
                >
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="p-2 rounded-xl bg-white/10 text-amber-400 shrink-0">
                            <Bell size={18} />
                        </div>
                        <div className="min-w-0">
                            <p className="text-xs font-semibold truncate">Enable Gate & Visitor Alerts</p>
                            <p className="text-[11px] text-white/70">Instant phone notifications when guests arrive</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                        <button
                            onClick={handleEnableQuick}
                            disabled={loading}
                            className="px-3 py-1.5 bg-[#A76D60] hover:bg-[#8A5A4F] text-white rounded-lg text-xs font-medium transition shadow-xs"
                        >
                            {loading ? "..." : "Enable"}
                        </button>
                        <button
                            onClick={handleDismiss}
                            className="p-1 text-white/60 hover:text-white transition"
                            title="Dismiss"
                        >
                            <X size={15} />
                        </button>
                    </div>
                </div>
            )}

            <NotificationModal isOpen={modalOpen} onClose={() => setModalOpen(false)} />
        </>
    );
}
